import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useScrolled } from "@/hooks/useReveal";

const links = [
  { label: "Menu", href: "#menu" },
  { label: "Our Story", href: "#about" },
  { label: "Reviews", href: "#reviews" },
  { label: "Gallery", href: "#gallery" },
  { label: "Visit", href: "#visit" },
];

export default function Nav() {
  const scrolled = useScrolled(20);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const handleNav = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#F4F0EA]/90 border-b border-[#132A1C]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 flex items-center justify-between h-20">
        <a
          href="#top"
          data-testid="nav-logo"
          onClick={(e) => { e.preventDefault(); handleNav("#top"); }}
          className={`font-serif-display text-2xl md:text-[28px] tracking-tight transition-colors ${
            scrolled ? "text-[#132A1C]" : "text-[#F4F0EA]"
          }`}
        >
          Cafe <span className={`italic transition-colors ${scrolled ? "text-[#2C4C3B]" : "text-[#D4A373]"}`}>Anh</span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => handleNav(l.href)}
              className={`text-[13px] tracking-[0.18em] uppercase transition-colors ${
                scrolled
                  ? "text-[#132A1C]/80 hover:text-[#132A1C]"
                  : "text-[#F4F0EA]/85 hover:text-[#F4F0EA]"
              }`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:block">
          <button
            data-testid="nav-cta-reserve"
            onClick={() => handleNav("#visit")}
            className={`px-6 py-3 text-[12px] tracking-[0.2em] uppercase transition-colors ${
              scrolled
                ? "bg-[#132A1C] text-[#F4F0EA] hover:bg-[#0D1E13]"
                : "bg-[#F4F0EA] text-[#132A1C] hover:bg-[#D4A373]"
            }`}
          >
            Reserve
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className={`md:hidden -mr-2 w-11 h-11 flex items-center justify-center transition-colors ${
            scrolled ? "text-[#132A1C]" : "text-[#F4F0EA]"
          }`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="md:hidden bg-[#F4F0EA] border-t border-[#132A1C]/10"
        >
          <div className="px-6 py-8 flex flex-col gap-6">
            {links.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => handleNav(l.href)}
                className="text-left font-serif-display text-3xl text-[#132A1C]"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-mobile-cta"
              onClick={() => handleNav("#visit")}
              className="mt-4 px-6 py-4 text-[12px] tracking-[0.2em] uppercase bg-[#132A1C] text-[#F4F0EA]"
            >
              Reserve a table
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
